import { LocalStore } from '@/platform/localStore';
import type { StoryProgress } from '@/contracts/family';
import type { Story } from '@/contracts/content';
import { SyncService } from './sync';

/**
 * Reading progress (spec §14.2 StoryProgress).
 *
 * Invariants:
 *  - Page ZERO is a real saved position. The old build only wrote progress
 *    once past the first page (spec §19.1).
 *  - `firstCompletedAt` is set exactly once. Re-reading a story never
 *    increments a completion count (spec §20.2 "Finish the same story
 *    repeatedly").
 *  - Creative access follows participation, not a score.
 */

function key(childId: string, storyId: string) {
  return `${childId}::${storyId}`;
}

export const ProgressService = {
  async get(account: string, childId: string, storyId: string): Promise<StoryProgress | undefined> {
    return LocalStore.get<StoryProgress>('progress', account, key(childId, storyId));
  },

  async listForChild(account: string, childId: string): Promise<StoryProgress[]> {
    const all = await LocalStore.list<StoryProgress>('progress', account);
    return all.filter((p) => p.childId === childId);
  },

  async ensure(account: string, childId: string, story: Story): Promise<StoryProgress> {
    const existing = await ProgressService.get(account, childId, story.id);
    if (existing) return existing;
    const fresh: StoryProgress = {
      id: `prog_${crypto.randomUUID()}`,
      ownerUserId: account,
      childId,
      storyId: story.id,
      versionId: story.currentVersion.versionId,
      currentPageId: story.currentVersion.pages[0].id,
      activityCompleted: false,
      participationMethod: null,
      artUnlocked: false,
      firstCompletedAt: null,
      updatedAt: new Date().toISOString(),
      revision: 1,
    };
    await LocalStore.put('progress', account, key(childId, story.id), fresh);
    void SyncService.queue('progress', key(childId, story.id), { childId });
    return fresh;
  },

  /** Saves the current page. Page zero included. */
  async setPage(account: string, childId: string, story: Story, pageId: string) {
    const p = await ProgressService.ensure(account, childId, story);
    const next: StoryProgress = {
      ...p,
      currentPageId: pageId,
      updatedAt: new Date().toISOString(),
      revision: p.revision + 1,
    };
    await LocalStore.put('progress', account, key(childId, story.id), next);
    void SyncService.queue('progress', key(childId, story.id), { childId });
    return next;
  },

  /**
   * Records participation and unlocks creating. Idempotent: calling it again
   * on a re-read leaves `firstCompletedAt` alone.
   */
  async completeParticipation(
    account: string,
    childId: string,
    story: Story,
    method: NonNullable<StoryProgress['participationMethod']>,
  ) {
    const p = await ProgressService.ensure(account, childId, story);
    const next: StoryProgress = {
      ...p,
      activityCompleted: true,
      participationMethod: p.participationMethod ?? method,
      artUnlocked: true,
      firstCompletedAt: p.firstCompletedAt ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      revision: p.revision + 1,
    };
    await LocalStore.put('progress', account, key(childId, story.id), next);
    void SyncService.queue('progress', key(childId, story.id), { childId });
    return next;
  },

  /** Unique stories completed — counted once each, never per session. */
  async uniqueCompleted(account: string, childId: string): Promise<number> {
    const all = await ProgressService.listForChild(account, childId);
    return new Set(all.filter((p) => p.firstCompletedAt).map((p) => p.storyId)).size;
  },
};
